import { createFileRoute } from '@tanstack/react-router';
import Hero from '../../components/Hero';
import { basicHead } from '../../utility/head';

export const Route = createFileRoute('/_marketing/subprocessors')({
  component: RouteComponent,
  ssr: true,
  loader: ({ location }) => ({
    pathname: location.pathname,
    seo: {
      title: 'Sub-processors - Stark Crawl',
      description:
        'The current list of third-party sub-processors that Stark Crawl uses to deliver its services, including their purpose, data categories processed, and processing location.',
      keywords: 'Stark Crawl sub-processors, GDPR, data processing, DPA',
    },
  }),
  head: basicHead,
});

type Subprocessor = {
  name: string;
  service: string;
  purpose: string;
  dataCategories: string;
  location: string;
  transferMechanism: string;
  website: string;
};

const subprocessors: Subprocessor[] = [
  {
    name: 'Amazon Web Services, Inc.',
    service: 'AWS',
    purpose:
      'Cloud infrastructure hosting our application, databases, storage, authentication, message queues, and language-model inference.',
    dataCategories: 'Customer content, account data, and application logs.',
    location:
      'eu-west-2 (London, United Kingdom) for application infrastructure; eu-west-1 (Ireland) and eu-west-2 (London) for language-model inference. There is no fallback to non-EU regions.',
    transferMechanism:
      'Data remains within the United Kingdom and European Economic Area. AWS does not use, and contractually requires foundation-model providers not to use, customer prompts or completions to train any models.',
    website: 'https://aws.amazon.com/compliance/gdpr-center/',
  },
  {
    name: 'Pinecone Systems, Inc.',
    service: 'Pinecone',
    purpose:
      'Managed vector database — stores dense and sparse embeddings of your content and associated metadata for hybrid retrieval.',
    dataCategories:
      'Vector embeddings of content chunks and associated chunk metadata (such as source identifiers and document references).',
    location: 'United States',
    transferMechanism:
      'EU Standard Contractual Clauses and the UK International Data Transfer Addendum. Where Pinecone is currently certified under the EU–US Data Privacy Framework, that framework provides an additional safeguard.',
    website: 'https://www.pinecone.io/security/',
  },
  {
    name: 'Stripe Payments Europe, Ltd.',
    service: 'Stripe',
    purpose:
      'Payment processing for subscriptions, invoicing, and tax handling. The relationship is split: Stripe acts as our Sub-processor for billing-contact metadata, and as an independent Controller for full payment-card data, which we never receive or store.',
    dataCategories:
      'Billing contact details (account email and company name) and Stripe customer/subscription/invoice identifiers. Stark Crawl does not collect or transmit full card data — Stripe collects card data directly from the cardholder as an independent Controller.',
    location: 'Ireland and United States',
    transferMechanism:
      'EU Standard Contractual Clauses and the UK International Data Transfer Addendum. Where Stripe is currently certified under the EU–US Data Privacy Framework, that framework provides an additional safeguard.',
    website: 'https://stripe.com/gb/privacy',
  },
  {
    name: 'Functional Software, Inc. (Sentry)',
    service: 'Sentry',
    purpose:
      'Application error monitoring and performance telemetry for incident detection and debugging.',
    dataCategories:
      'Technical error data, stack traces, pseudonymous user identifiers, IP address, browser metadata.',
    location: 'United States',
    transferMechanism:
      'EU Standard Contractual Clauses and the UK International Data Transfer Addendum. Where Sentry is currently certified under the EU–US Data Privacy Framework, that framework provides an additional safeguard.',
    website: 'https://sentry.io/privacy/',
  },
  {
    name: 'Cloudflare, Inc.',
    service: 'Cloudflare',
    purpose:
      'DNS management for customer custom domains via the Cloudflare Custom Hostnames API. Stark Crawl application traffic is served via AWS CloudFront and Route 53; no application or customer content traffic transits Cloudflare.',
    dataCategories:
      'Customer-supplied custom-hostname records and the metadata required to provision SSL certificates for those hostnames.',
    location: 'Global anycast network with EU and US presence',
    transferMechanism:
      'EU Standard Contractual Clauses and the UK International Data Transfer Addendum. Where Cloudflare is currently certified under the EU–US Data Privacy Framework, that framework provides an additional safeguard.',
    website: 'https://www.cloudflare.com/en-gb/privacypolicy/',
  },
  {
    name: 'Mendable AI, Inc. (Firecrawl)',
    service: 'Firecrawl',
    purpose:
      'Web-page fetching and conversion to markdown when a Controller adds a public URL as a document source. Controllers should not submit URLs they consider confidential — the URL itself, including any tokens or path segments, is sent to Firecrawl.',
    dataCategories:
      'URLs submitted by the Controller and the public web content returned for those URLs. No account credentials or private application content are sent.',
    location: 'United States',
    transferMechanism:
      'EU Standard Contractual Clauses and the UK International Data Transfer Addendum. Where Firecrawl is currently certified under the EU–US Data Privacy Framework, that framework provides an additional safeguard.',
    website: 'https://www.firecrawl.dev/privacy-policy',
  },
];

function RouteComponent() {
  return (
    <section className="py-[3.75rem] lg:py-20 px-5 border-b border-grey-t-800 grow">
      <Hero
        breadcrumb="Sub-processors"
        title="Sub-processors"
        pathname="/subprocessors"
      />
      <article className="mt-5 space-y-3 lg:mt-[3.75rem] max-w-[57.5rem] mx-auto prose prose-lg">
        <p className="text-sm text-gray-500">Last updated: May 2026</p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          1. Purpose of This Page
        </h2>
        <p>
          Stark Crawl engages a small number of trusted third parties
          (&quot;Sub-processors&quot;) to help us deliver our services. This
          page lists every Sub-processor that may process Personal Data
          processed on behalf of our customers, in line with section 7 of our{' '}
          <a href="/data-processing-agreement">Data Processing Agreement</a>.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          2. Current Sub-processors
        </h2>
        <p>
          The table below reflects the Sub-processors currently engaged.
          Customers can subscribe to changes by emailing{' '}
          <a href="mailto:privacy@starkcrawl.com">privacy@starkcrawl.com</a>.
        </p>

        <div className="overflow-x-auto mt-5">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left border-b border-grey-t-800">
                <th className="py-3 pr-4 font-semibold align-top">
                  Sub-processor
                </th>
                <th className="py-3 pr-4 font-semibold align-top">Purpose</th>
                <th className="py-3 pr-4 font-semibold align-top">
                  Data categories
                </th>
                <th className="py-3 pr-4 font-semibold align-top">Location</th>
                <th className="py-3 pr-4 font-semibold align-top">
                  Transfer mechanism
                </th>
              </tr>
            </thead>
            <tbody>
              {subprocessors.map((sp) => (
                <tr
                  key={sp.name}
                  className="border-b border-grey-t-800 align-top"
                >
                  <td className="py-4 pr-4">
                    <a
                      href={sp.website}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="font-semibold underline"
                    >
                      {sp.service}
                    </a>
                    <div className="text-xs text-gray-500 mt-1">{sp.name}</div>
                  </td>
                  <td className="py-4 pr-4">{sp.purpose}</td>
                  <td className="py-4 pr-4">{sp.dataCategories}</td>
                  <td className="py-4 pr-4">{sp.location}</td>
                  <td className="py-4 pr-4">{sp.transferMechanism}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          3. Customer-Authorised Integrations
        </h2>
        <p>
          Stark Crawl allows Controllers to connect their own third-party
          accounts as document sources — for example Google Drive, Microsoft
          OneDrive, Dropbox, and Notion. These providers are <strong>not</strong>{' '}
          our Sub-processors: they are independent controllers of the source
          data, and the Controller authorises Stark Crawl to retrieve content
          from them via OAuth. Once content is retrieved, it is processed by
          the Sub-processors listed in section 2 above. The Controller is
          responsible for the lawful basis on which it grants Stark Crawl access
          to those sources.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          4. Service Providers Not Listed Above
        </h2>
        <p>
          Stark Crawl also engages other providers — including Mixpanel,
          Google Analytics, and Google Ads — for our own product
          analytics and advertising measurement. For
          these tools, Stark Crawl is the Controller of the limited operational
          and product-usage data collected about end users of our marketing
          and product surfaces; we do not route customer-uploaded content
          through them on behalf of our customers. They are therefore not
          Sub-processors under this DPA, and are disclosed in our{' '}
          <a href="/privacy">Privacy Policy</a>.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          5. Notification of Changes
        </h2>
        <p>
          We will give customers at least 30 days&apos; prior notice before
          adding or replacing a Sub-processor that processes Personal Data on
          their behalf. Notice will be given by updating this page and by email
          to the registered account contact. Customers may object to a proposed
          change in writing within 14 days of receiving notice, in accordance
          with section 7.2 of the{' '}
          <a href="/data-processing-agreement">Data Processing Agreement</a>.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          6. Affiliates
        </h2>
        <p>
          Where a Sub-processor uses its own group affiliates to deliver the
          service (for example AWS regional entities), those
          affiliates are bound by the same data-processing terms as the named
          Sub-processor.
        </p>

        <h2 className="text-h2 font-semibold font-grotesque mt-10 mb-5">
          7. Contact
        </h2>
        <p>
          For any questions about our Sub-processors, our cross-border transfer
          mechanisms, or to request a copy of the relevant Standard Contractual
          Clauses, please contact{' '}
          <a href="mailto:privacy@starkcrawl.com">privacy@starkcrawl.com</a>.
        </p>
      </article>
    </section>
  );
}
